import Select from "../Inputs/Select";
import Input from "../Inputs/Input";
import IncomeYearlyIncrease from "./IncomeYearlyIncrease";
import { InvestmentIncome, Person, ScenarioSettings } from "src/types";
import { useState } from "react";
import Button from "../Inputs/Button";
import StartEndSelect, { startEndToYear } from "./StartEndSelect";
import InvestmentSchedule from "./InvestmentSchedule";
import { calculateInvestmentSchedule } from "src/calculator/investment-income";
import { printNumber } from "src/utils";

interface Props {
  income: InvestmentIncome;
  people: Person[];
  setIncome: (income: InvestmentIncome) => void;
  settings: ScenarioSettings;
}

const InvestmentIncomeInfo = ({
  people,
  income,
  setIncome,
  settings,
}: Props) => {
  const options = [...people] as any[];
  if (people.length == 2) {
    options.push({ name: "Joint", id: -1 });
  }

  const [openYears, setOpenYears] = useState(false);

  const schedule = calculateInvestmentSchedule(
    settings,
    income,
    people,
    new Date().getFullYear() + settings.maxYearsShown,
  );

  const principalUsed =
    income.principalType === "Calculated"
      ? Math.round(
        schedule?.find(
          (i) => i.year === startEndToYear(income.start, people, settings),
        )?.endAmount,
      )
      : income.principal || 0;

  let usedIncome = null;
  if (income.principalType === "Calculated") {
    const scheduleValue = schedule?.find(
      (i) =>
        i.year === startEndToYear(income.start, people, settings) ||
        new Date().getFullYear(),
    )?.endAmount;
    if (scheduleValue) {
      usedIncome = Math.round(
        (scheduleValue * (income.withdrawalRate || 0)) / 100,
      );
    }
  } else
    usedIncome = ((income.principal || 0) * (income.withdrawalRate || 0)) / 100;

  return (
    <>
      <div className="grow">
        <div className="flex flex-col gap-4">
          {people.length > 1 && (
            <Select
              options={options}
              selected={
                income.personId == -1
                  ? { name: "Joint", id: -1 }
                  : people[income.personId]
              }
              width="w-64!"
              setSelected={(i) => {
                let newIncome = {
                  ...income,
                  personId: i.id,
                };
                if (
                  ["age", "death", "retirement"].includes(income.start.type) &&
                  i.id !== -1
                ) {
                  newIncome.start = { ...income.start, personId: i.id } as any;
                }
                setIncome(newIncome);
              }}
              label="Person"
            />
          )}
          <Input
            label="Title"
            subtype="text"
            width="w-64!"
            size="income"
            value={income.name}
            setValue={(name) => setIncome({ ...income, name })}
          />
          <div className="flex items-center gap-1">
            <div className="text-sm text-[#344054] min-w-36">Principal </div>
            <Select
              options={[
                { name: "Manual", id: "Manual" },
                { name: "Calculated", id: "Calculated" },
              ]}
              selected={{
                name: income.principalType,
                id: income.principalType,
              }}
              width="w-36!"
              setSelected={(i) => setIncome({ ...income, principalType: i.id })}
              label=""
            />
            {income.principalType === "Calculated" ? (
              <Button type="primary" onClick={() => setOpenYears(true)}>
                Edit
              </Button>
            ) : (
              <Input
                label=""
                subtype="money"
                size="income"
                width="w-[120px]!"
                value={income.principal}
                setValue={(name) => setIncome({ ...income, principal: name })}
              />
            )}
          </div>

          <StartEndSelect
            label="Start"
            shownOptions={[
              ...(income.personId != -1
                ? [["death", income.personId]]
                : (people.map((p) => ["death", p.id] as any) as any)),
              ...(income.personId != -1
                ? [["age", income.personId]]
                : (people.map((p) => ["age", p.id] as any) as any)),
              ["calendar-year"],
              ["#-of-years"],
              ...(income.personId != -1
                ? [["retirement", income.personId]]
                : (people.map((p) => ["retirement", p.id] as any) as any)),
              ["already-receiving"],
            ]}
            value={income.start}
            setValue={(start) => setIncome({ ...income, start })}
            settings={settings}
          />
          <StartEndSelect
            label="End"
            shownOptions={[
              ...(income.personId != -1
                ? [["death", income.personId]]
                : (people.map((p) => ["death", p.id] as any) as any)),
              ...(income.personId != -1
                ? [["age", income.personId]]
                : (people.map((p) => ["age", p.id] as any) as any)),
              ["calendar-year"],
              ["#-of-years"],
              ["end-of-plan"],
              ...(income.personId != -1
                ? [["retirement", income.personId]]
                : (people.map((p) => ["retirement", p.id] as any) as any)),
            ]}
            value={income.end}
            setValue={(end) => setIncome({ ...income, end })}
            settings={settings}
          />
          <div className="flex gap-2 items-center">
            <Input
              label="Withdrawal Rate"
              subtype="percent"
              size="income"
              width="w-[100px]!"
              value={income.withdrawalRate}
              setValue={(name) =>
                setIncome({ ...income, withdrawalRate: name })
              }
            />
            <div className="w-full">of {printNumber(principalUsed)}</div>
          </div>

          <Input
            label="Income"
            subtype="money"
            size="income"
            disabled
            value={usedIncome}
            setValue={() => { }}
          />

          <IncomeYearlyIncrease
            labels={false}
            increase={income.yearlyIncrease}
            setYearlyIncrease={(yearlyIncrease) =>
              setIncome({ ...income, yearlyIncrease })
            }
          />

          <Select
            options={[
              { name: "Taxable", id: "Taxable" },
              { name: "Tax-Deferred", id: "Tax-Deferred" },
              { name: "Tax-Free", id: "Tax-Free" },
            ]}
            selected={{
              name: income.taxType,
              id: income.taxType,
            }}
            width="w-64!"
            setSelected={(i) => setIncome({ ...income, taxType: i.id })}
            label="Tax Status"
          />
        </div>
      </div>

      <InvestmentSchedule
        openYears={openYears}
        setOpenYears={setOpenYears}
        settings={settings}
        people={people}
        income={income}
        setIncome={setIncome}
      />
    </>
  );
};

export default InvestmentIncomeInfo;
